import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { CustomCursorEvent } from 'app/events/CustomCursorEvent';
import { DirectionType, AxisType } from 'app/enum/Geometry';
import Config from 'config/settings';
import SiteTransitions from 'app/services/SiteTransitions';

import { MouseCursorView } from './MouseCursorView';
import { MouseTracking } from './MouseTracking';

/**
 * Global custom-cursor / pointer-state controller (registered as 'Mouse' on
 * the body selector, and exposed as globals.mouseController for
 * src/interaction/InertiaDrag.ts). Orchestrates the per-concern sub-objects
 * in this folder (see mouse.types.ts for the decomposition pattern,
 * established in src/app/views/work/). Keeps: the lifecycle hooks, the full
 * public surface used by AppController / UIController /
 * VerticalDragController / the view controllers / DragPanel / DragableVideo /
 * DragBreakpoints / the work Pixi tiles, every Events.on(...) registration,
 * and all cross-controller / SiteTransitions access.
 */
export class MouseController extends Controller {
	public guideFollowActive: boolean = false;

	//Per-concern sub-objects (see mouse.types.ts for the pattern):
	private _view: MouseCursorView;
	private _tracking: MouseTracking;

	constructor(id: string) {
		super(id);

		this._tracking = new MouseTracking(this.DOM);

		this._view = new MouseCursorView(this.DOM, {
			getMouseCords: () => this._tracking.getMouseCords(),
			isTouchActive: () => this._tracking.touchActive,
			getDelayedX: () => this._tracking.delayedX,
			getDelayedY: () => this._tracking.delayedY,
			isGuideFollowActive: () => this.guideFollowActive,
			onHomeExploreMouseOver: () => SiteTransitions.homeExploreMouseOver(),
			onHomeExploreMouseOut: () => SiteTransitions.homeExploreMouseOut()
		});
	}

	public get velocityX(): number {
		return this._tracking.velocityX;
	}

	public get velocityY(): number {
		return this._tracking.velocityY;
	}

	public get direction(): DirectionType {
		return this._tracking.direction;
	}

	public get directionAxis(): AxisType {
		return this._tracking.directionAxis;
	}

	public get moveAxis(): AxisType {
		return this._tracking.moveAxis;
	}

	public init(): void {
		this._tracking.init();
		this._view.createInertias();

		globals.mouseController = this;

		this._view.queryBody();
	}

	public DOMReady(): void {
		this._view.queryRefs(this.container);

		if (Config.allowMouse) {
			Events.on('pointerover', '.ui_hidecursor', this._mouseOver.bind(this));
			Events.on('pointerout', '.ui_hidecursor', this._mouseOut.bind(this));
			Events.on('pointerdown', this.container, this._onMouseDown.bind(this));
			Events.on('pointerup', this.container, this._onMouseUp.bind(this));
			Events.on('pointerover', '.ui_synccursor', this._syncMouseOver.bind(this));
			Events.on('pointerout', '.ui_synccursor', this._syncMouseOut.bind(this));
			Events.on('pointermove', this.container, this._onMouseMove.bind(this));

			document.documentElement.addEventListener('mouseleave', () => {
				this._view.setOutOfBrowser(true);
			});

			document.documentElement.addEventListener('mouseenter', () => {
				this._view.setOutOfBrowser(false);
			});
		}

		if (Config.allowTouch) {
		}

		Events.on(CustomCursorEvent, this._onCursorEvent.bind(this));
	}

	/**
	 * Check is last pointer event was touch
	 * @returns
	 */
	public lastPointerWasTouch(): boolean {
		return this._tracking.lastPointerWasTouch();
	}

	public removeLock(): void {
		this._view.syncMouseOut(null);
	}

	public getXVelocityAvg() {
		return this._tracking.getXVelocityAvg();
	}

	public tick(): void {
		this._tracking.tick();
		this._view.tick();
	}

	public resize(): void {
		this._tracking.resize();
		this._view.resize();
	}

	/** When showing nav menu stop special state and save if active at time of pause */
	public pause(): void {
		this._view.pause();
	}

	/** When hiding nav menu show 'drag' state if it was showing at the time of pausing */
	public unpause(): void {
		this._view.unpause();
	}

	/** Background can be affected by the parallax in home / work sections */
	public setDarkTheme(isDarkTheme: boolean): void {
		this._view.setDarkTheme(isDarkTheme);
	}

	public addArrowGuide(type: string) {
		this._view.addArrowGuide(type);
	}

	public removeArrowGuides() {
		this._view.removeArrowGuides();
	}

	/** Background can be affected by the parallax in home / work sections */
	public setHiddenState(hidden: boolean, force = false): void {
		this._view.setHiddenState(hidden, force);
	}

	public setClickableState(clickable: boolean): void {
		this._view.setClickableState(clickable);
	}

	public transition(delay: number = 200) {
		this._view.transition(delay);
	}

	/**
	 * Set the special drag cursor to be active on sections that can be dragged
	 * @param {boolean} active - Turn on / off.
	 * @param {boolean} [resetPauseHoldingFlag=true] - Reset any flags that hav have been set by pause action.
	 */
	public setDragCursorActive(active: boolean, resetPauseHoldingFlag: boolean = true): void {
		this._view.setDragCursorActive(active, resetPauseHoldingFlag);
	}

	public resetToHorizontal(): void {
		this._view.resetToHorizontal();
	}

	public startVerticalScroll(): void {
		this._view.startVerticalScroll();
	}

	public stopVerticalScroll(): void {
		this._view.stopVerticalScroll();
	}

	public setBothActive(active: boolean): void {
		this._view.setBothActive(active);
	}

	/**
	/* Set the special drag cursor to be active on sections that can be dragged (I.E grey on sections because of the white/black mix)
	/* @param {string} colour - Color class to change the curose to
	*/
	public setColour(colour: string): void {
		this._view.setColour(colour);
	}

	public setDragDown(isDown: boolean, side: string = null, animationTime: number = 0.2): void {
		//NOTE: animationTime is never read — parameter kept for signature compat

		this._tracking.setDragDown(isDown);

		if (globals.lastCordsFromTouch) {
			return;
		}

		this._view.applyDragDown(isDown, side);
	}

	public getMouseCords(): any {
		return this._tracking.getMouseCords();
	}

	public mouseIsDown(): boolean {
		return this._tracking.mouseIsDown();
	}

	public homeIntroStart(): void {
		this._view.homeIntroStart();
	}

	public homeIntroHover(): void {
		this._view.homeIntroHover();
	}

	public homeIntroMouseOut(): void {
		this._view.homeIntroMouseOut();
	}

	public introComplete(): void {
		this._view.introComplete();
	}

	//PRIVATE — handler bodies delegate inward (registrations above)
	//----------------------------------------------------------------------------

	private _onMouseDown(e: PointerEvent): void {
		this._tracking.onMouseDown(e);
	}

	private _onMouseUp(e: PointerEvent): void {
		this._tracking.onMouseUp(e);
	}

	private _onMouseMove(e: PointerEvent): void {
		this._tracking.onMouseMove(e);
	}

	private _mouseOver(e: MouseEvent): void {
		this._view.mouseOver(e);
	}

	private _mouseOut(e: MouseEvent): void {
		this._view.mouseOut(e);
	}

	private _syncMouseOver(e: MouseEvent): void {
		this._view.syncMouseOver(e);
	}

	private _syncMouseOut(e: MouseEvent = null): void {
		this._view.syncMouseOut(e);
	}

	/** Pickup event form other modules/controller to activate the cursor */
	private _onCursorEvent(e: CustomCursorEvent): void {
		if (e.type === 'hitarea') {
			if (e.theme) {
				this.setColour(e.theme);
			}
		} else if (e.type === 'drag') {
			this.setDragDown(e.active);
		}
	}
}
